import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors } from 'src/config/colors';
import { typography } from 'src/config/typography';
import { Button } from 'src/common/components/Button';
import { CourseEvent } from 'src/common/models/event';
import { media } from 'src/config/breakpoints';
import ChevronIcon from 'src/assets/icons/ChevronIcon';
import { CourseEventSelectPanelItem } from 'src/public/components/course-page/CourseEventSelectPanel/CourseEventSelectPanelItem';
import { CourseEventActions } from 'src/public/components/course-page/CourseEventSelectPanel/CourseEventActions';
import { Course } from 'src/common/models/course';
import { StyleProps } from 'src/lib/styleProps';

const Items = styled.div`
  margin: 0 -1rem 0.5rem;
  padding: 0.0625rem 0.4375rem;
  overflow: hidden;

  ${media.w.min.px768(css`
    padding-bottom: 0.375rem;
    padding-top: 0.3125rem;
    margin-bottom: 0;
  `)};
`;

const ShowAllButton = styled(Button)`
  color: ${colors.blue07};
  font-size: 0.625rem;
  font-weight: 300;
  line-height: 0.875rem;

  svg {
    color: ${colors.blue};
    margin-left: 0.5rem;
  }
`;

const ShowAllButtonContainer = styled.div`
  margin-top: 0.75rem;
  text-align: center;
`;

const Title = styled.div`
  color: ${colors.blue};
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1rem;
  line-height: 1.375rem;
  margin-top: 1.125rem;
  text-align: center;

  ${media.w.max.px768(css`
    display: none;
  `)};
`;

export interface CourseEventSelectPanelProps {
  course: Course;
  courseEvents: CourseEvent[];
  selectedCourseEvent: CourseEvent;
  onChange: (value: CourseEvent) => void;
}

export const CourseEventSelectPanel: FC<StyleProps & CourseEventSelectPanelProps> = ({
  className,
  style,
  course,
  courseEvents,
  selectedCourseEvent,
  onChange,
}) => {
  const [showAll, setShowAll] = useState(false);
  const hasMultipleEvents = courseEvents.length > 1;

  return (
    <div className={className} style={style}>
      {selectedCourseEvent && !showAll && (
        <CourseEventSelectPanelItem
          courseEvent={selectedCourseEvent}
          actions={
            <CourseEventActions
              course={course}
              selectedCourseEvent={selectedCourseEvent}
              courseEvents={courseEvents}
            />
          }
        />
      )}

      {showAll && (
        <>
          <Title>Wybierz termin</Title>
          <Items>
            {courseEvents.map((event) => (
              <CourseEventSelectPanelItem
                courseEvent={event}
                dimmed={!!selectedCourseEvent}
                key={event.id}
                onClick={() => onChange(event)}
                selected={selectedCourseEvent === event}
              />
            ))}
          </Items>

          <CourseEventActions
            course={course}
            selectedCourseEvent={selectedCourseEvent}
            courseEvents={courseEvents}
          />
        </>
      )}

      {hasMultipleEvents && (
        <ShowAllButtonContainer>
          <ShowAllButton kind="link" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Jeden termin' : 'Wszystkie terminy'}
            <ChevronIcon direction={showAll ? 'up' : 'down'} size={11} />
          </ShowAllButton>
        </ShowAllButtonContainer>
      )}
    </div>
  );
};
